import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

// Connects to data-controller="entity-grant-form"
export default class extends Controller {
  static targets = [
    "granteeSelect",
    "granteeType",
    "granteeId",
    "actionField",
    "actionLabel",
    "messageRow",
    "shareRow",
  ];

  declare readonly granteeSelectTarget: HTMLSelectElement;
  declare readonly granteeTypeTarget: HTMLInputElement;
  declare readonly granteeIdTarget: HTMLInputElement;
  declare readonly actionFieldTarget: HTMLInputElement;
  declare readonly actionLabelTarget: HTMLElement;
  declare readonly messageRowTarget: HTMLElement;
  declare readonly shareRowTarget: HTMLElement;

  connect() {
    // Listen for changes on the tom-select element
    this.granteeSelectTarget.addEventListener(
      "change",
      this.parseCompositeId.bind(this),
    );
  }

  handleSubmit(event: Event) {
    // Parse the composite ID before form submission
    this.parseCompositeId();
  }

  onGranteeSelected() {
    const hasSelection =
      this.granteeSelectTarget.value && this.granteeSelectTarget.value !== "";

    if (hasSelection) {
      // Show the message textarea and share button
      this.messageRowTarget.classList.remove("hidden");
      this.shareRowTarget.classList.remove("hidden");
      this.shareRowTarget.classList.add("flex");
    } else {
      // Hide the message textarea and share button
      this.messageRowTarget.classList.add("hidden");
      this.shareRowTarget.classList.add("hidden");
      this.shareRowTarget.classList.remove("flex");
    }
  }

  setAction(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    const actionValue = target.dataset.actionValue;

    if (!actionValue) return;

    // Update the hidden field
    this.actionFieldTarget.value = actionValue;

    // Update the label
    if (actionValue === "read") {
      this.actionLabelTarget.innerHTML = `
        <span class="max-sm:hidden flex flex-row gap-2"><svg class="w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
        ${i18n.t('entity_grant_form.can')} </span>${i18n.t('entity_grant_form.view')}
      `;
    } else if (actionValue === "update") {
      this.actionLabelTarget.innerHTML = `
        <span class="max-sm:hidden flex flex-row gap-2"><svg class="w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
        ${i18n.t('entity_grant_form.can')} </span>${i18n.t('entity_grant_form.edit')}
      `;
    } else if (actionValue === "manage") {
      this.actionLabelTarget.innerHTML = `
        <span class="max-sm:hidden flex flex-row gap-2"><svg class="w-4 h-4 shrink-0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shield-check-icon lucide-shield-check"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
        ${i18n.t('entity_grant_form.can')} </span>${i18n.t('entity_grant_form.manage')}
      `;
    }

    // Close the dropdown
    const dropdown = target.closest(".dropdown");
    if (dropdown) {
      const label = dropdown.querySelector("label");
      if (label) {
        label.blur();
      }
    }
  }

  parseCompositeId() {
    const compositeValue = this.granteeSelectTarget.value;

    if (!compositeValue || compositeValue === "") {
      this.granteeTypeTarget.value = "";
      this.granteeIdTarget.value = "";
      return;
    }

    // Split the composite ID: "User:123" or "Group:456"
    const parts = compositeValue.split(":");

    if (parts.length === 2) {
      const [type, id] = parts;
      this.granteeTypeTarget.value = type;
      this.granteeIdTarget.value = id;
    }
  }
}
